import { supabase } from '../supabase/config';

// Debounce helper
export const loadChatsListDebounced = (() => {
  let timeout;
  return () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      window.loadChatsList();
    }, 300); // Wait 300ms before actually loading
  };
})();

export const openChatsListPanel = async () => {
  const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
  if (!currentUserId) {
    window.showToast('Please log in to access chats', 'error');
    return;
  }
  
  // Close profile panel if open
  if (window.setIsProfilePanelOpen) {
    window.setIsProfilePanelOpen(false);
  }
  
  // Use React state to open the panel
  if (window.setIsChatsListPanelOpen) {
    window.setIsChatsListPanelOpen(true);
  }
  
  setTimeout(() => {
    window.loadChatsList();
  }, 50);
};

export const closeChatsListPanel = () => {
  if (window.setChatsListPanelAnimating) {
    window.setChatsListPanelAnimating('exiting');
  }
  setTimeout(() => {
    if (window.setIsChatsListPanelOpen) {
      window.setIsChatsListPanelOpen(false);
    }
    if (window.setChatsListPanelAnimating) {
      window.setChatsListPanelAnimating('');
    }
  }, 300);
};

// Load chats list
export const loadChatsList = async () => {
  // 🆕 GUARD: Prevent multiple simultaneous loads
  if (window.isLoadingChatsList) {
    return;
  }
  
  window.isLoadingChatsList = true;
  
  const container = document.getElementById('chatsListContainer');
  if (!container) {
    window.isLoadingChatsList = false;
    return;
  }
  
// Show loading state
  container.innerHTML = `
    <div style="
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
    ">
      ${window.CustomLoadingSpinner({ size: 48, message: '' })}
    </div>
  `;
  
  try {
    const supabaseClient = window.supabase || supabase;
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    
    // Get all events where user is participant or organizer
    const { data: allEvents, error } = await supabaseClient
      .from('events')
      .select('event_id, title, participants, creator_id, chat_enabled, archived, place_name, place_icon, place_address, start_date_time, end_date_time')
      .or(`creator_id.eq.${currentUserId},participants.cs.{${currentUserId}}`)
      .eq('archived', false)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    const events = allEvents || [];
    if (events.length === 0) {
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
        ">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#dadce0" stroke-width="2" style="margin-bottom: 16px;">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <p style="margin: 0; color: #5f6368; font-size: 16px; font-weight: 500;">No chats yet</p>
          <p style="margin: 8px 0 0 0; color: #9e9e9e; font-size: 14px;">Join or create an event to start chatting</p>
        </div>
      `;
      
      window.hasUnreadChats = false;
      window.dispatchEvent(new CustomEvent('unreadChatsChanged'));
      return;
    }
    
    // 🆕 BATCH FETCH: Get last message for ALL events in ONE query
    const eventIds = events.map(e => e.event_id);
    
    const { data: allMessages } = await supabaseClient
      .from('messages')
      .select('event_id, message, created_at, user_id, read_by')
      .in('event_id', eventIds)
      .order('created_at', { ascending: false });
    
    // Group messages by event_id and get the latest one for each
    const lastMessagesByEvent = {};
    if (allMessages) {
      allMessages.forEach(msg => {
        if (!lastMessagesByEvent[msg.event_id]) {
          lastMessagesByEvent[msg.event_id] = msg;
        }
      });
    }
    
    // 🆕 BATCH FETCH: Get unique user IDs and fetch names in ONE query
    const uniqueUserIds = [...new Set(
      Object.values(lastMessagesByEvent)
        .map(msg => msg.user_id)
        .filter(id => id !== currentUserId)
    )];
    
    const userNamesMap = {};
    if (uniqueUserIds.length > 0) {
      const { data: users } = await supabaseClient
        .from('users')
        .select('id, name')
        .in('id', uniqueUserIds);
      
      if (users) {
        users.forEach(user => {
          userNamesMap[user.id] = user.name;
        });
      }
    }
    
    // Build chats with messages data
    const chatsWithMessages = events.map(event => {
      const lastMessage = lastMessagesByEvent[event.event_id] || null;
      
      return {
        event,
        lastMessage,
        lastMessageTime: lastMessage ? new Date(lastMessage.created_at).getTime() : 0
      };
    });

    // Sort by newest message first
    chatsWithMessages.sort((a, b) => b.lastMessageTime - a.lastMessageTime);

    // Track if there are any unread messages
    let hasAnyUnread = false;

    // Generate HTML for each chat (synchronously now - no more async calls!)
    const chatsHTML = chatsWithMessages.map(({ event, lastMessage }) => {
      let senderName = '';
      let isUnread = false;
      
      if (lastMessage) {
        if (lastMessage.user_id === currentUserId) {
          senderName = 'You';
        } else {
          const readBy = lastMessage.read_by || [];
          isUnread = !readBy.includes(currentUserId);
          
          if (isUnread) {
            hasAnyUnread = true;
          }
          
          senderName = userNamesMap[lastMessage.user_id] || 'Someone';
        }
      }
      
      const isOrganizer = event.creator_id === currentUserId;
      const lastMessageText = lastMessage ? `${senderName}: ${lastMessage.message}` : 'No messages yet';
      const lastMessageTime = lastMessage ? window.formatTimeAgo(lastMessage.created_at) : '';
      
      // Truncate message text
      let displayMessage = lastMessageText;
      if (displayMessage.length > 40) {
        displayMessage = displayMessage.substring(0, 37) + '...';
      }

      // Format address
      const placeAddress = event.place_address || '';
      let shortAddress = '';

      if (placeAddress) {
        const parts = placeAddress.split(',').map(p => p.trim());
        let street = '';
        let city = '';
        
        if (parts.length >= 2) {
          street = parts[0];
          city = parts.length >= 3 ? parts[parts.length - 2] : parts[parts.length - 1];
        } else if (parts.length === 1) {
          shortAddress = parts[0];
        }
        
        if (street && city) {
          shortAddress = `${street}, ${city}`;
        } else if (city) {
          shortAddress = city;
        } else if (street) {
          shortAddress = street;
        }
        
        if (shortAddress.length > 30) {
          shortAddress = shortAddress.substring(0, 27) + '...';
        }
      }
      
      const messageStyle = isUnread 
        ? 'font-weight: 600; color: #202124;' 
        : 'font-weight: 400; color: #5f6368;';
      
      return `
        <div
          onclick="window.openChatFromList('${event.event_id}', '${event.title.replace(/'/g, "\\'")}')"
          style="
            padding: 16px 20px;
            border-bottom: 1px solid #e8eaed;
            cursor: pointer;
            transition: background 0.2s;
            display: flex;
            gap: 12px;
            align-items: flex-start;
            background: ${isUnread ? '#f8f9fa' : 'white'};
          "
          onmouseover="this.style.background='#f1f3f4'"
          onmouseout="this.style.background='${isUnread ? '#f8f9fa' : 'white'}'"
        >
          <div style="
            width: 48px;
            height: 48px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: 2px;
            position: relative;
          ">
            ${event.place_icon ? `
              <img 
                src="${event.place_icon}" 
                alt="Place icon" 
                style="width: 48px; height: 48px; display: block;"
                onerror="this.style.display='none'; this.parentElement.innerHTML='📍';"
              />
            ` : `
              <div style="
                width: 40px;
                height: 40px;
                background: #757575;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 22px;
                box-shadow: 0 2px 6px rgba(0,0,0,0.2);
              ">📍</div>
            `}
            
            ${isUnread ? `
              <span style="
                position: absolute;
                top: 0;
                right: 0;
                width: 10px;
                height: 10px;
                background: #ea4335;
                border-radius: 50%;
                border: 2px solid white;
              "></span>
            ` : ''}
          </div>
          
          <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
              <h4 style="
                margin: 0;
                font-size: 15px;
                font-weight: ${isUnread ? '700' : '600'};
                color: #202124;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                flex: 1;
              ">${event.title}</h4>
              <div style="display: flex; align-items: center; gap: 6px; flex-shrink: 0;">
                ${isOrganizer ? `
                  <div style="
                    background: #e3f2fd;
                    color: #1967d2;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 600;
                  ">ORGANIZER</div>
                ` : ''}
                ${lastMessageTime ? `
                  <span style="
                    font-size: 11px; 
                    color: ${isUnread ? '#202124' : '#9e9e9e'};
                    font-weight: ${isUnread ? '600' : '400'};
                  ">${lastMessageTime}</span>
                ` : ''}
              </div>
            </div>
            
            <div style="display: flex; align-items: baseline; justify-content: space-between; gap: 8px;">
              <p style="margin: 0; font-size: 13px; ${messageStyle} overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;">
                ${displayMessage}
              </p>
              ${event.place_name ? `
                <span style="font-size: 11px; color: #9e9e9e; flex-shrink: 0; font-style: italic;">
                  ${event.place_name}
                </span>
              ` : ''}
            </div>
          </div>
        </div>
      `;
    });
    
    container.innerHTML = chatsHTML.join('');
    
    window.hasUnreadChats = hasAnyUnread;
    window.dispatchEvent(new CustomEvent('unreadChatsChanged'));
  } catch (error) {
    container.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        color: #ea4335;
        font-size: 14px;
      ">
        <p>Failed to load chats</p>
      </div>
    `;
  } finally {
    // 🆕 ALWAYS clear the loading flag
    window.isLoadingChatsList = false;
  }
};

// Quick unread check without full reload
export const checkUnreadMessagesQuick = async () => {
  // 🛑 Don't check if ANY chat is currently open
  if (window.currentOpenChatId) {
    return; // Silent guard, no logging
  }
  
  // Don't check if we're in the middle of marking messages as read
  if (window.isMarkingAsRead) {
    return;
  }
  
  if (window.isCheckingUnread) {
    return;
  }
  
  window.isCheckingUnread = true;
  
  try {
    const supabaseClient = window.supabase || supabase;
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    
    if (!currentUserId) {
      window.isCheckingUnread = false;
      return;
    }
    
    // Get user's events
    const { data: events } = await supabaseClient
      .from('events')
      .select('event_id')
      .or(`creator_id.eq.${currentUserId},participants.cs.{${currentUserId}}`)
      .eq('archived', false)
      .limit(50);
    
    if (!events || events.length === 0) {
      window.hasUnreadChats = false;
      window.dispatchEvent(new CustomEvent('unreadChatsChanged'));
      window.isCheckingUnread = false;
      return;
    }
    
    // Get all event IDs
    const eventIds = events.map(e => e.event_id);
    
    // Check for any unread messages
    const { data: unreadMessages } = await supabaseClient
      .from('messages')
      .select('id, user_id, read_by, event_id')
      .in('event_id', eventIds)
      .neq('user_id', currentUserId)
      .order('created_at', { ascending: false })
      .limit(100);
    
    const hasUnread = unreadMessages?.some(msg => {
      const readBy = msg.read_by || [];
      const isUnread = !readBy.includes(currentUserId);
      return isUnread;
    }) || false;
    
    const previousState = window.hasUnreadChats;
    window.hasUnreadChats = hasUnread;
    
    // Only trigger event if state actually changed
    if (previousState !== hasUnread) {
      window.dispatchEvent(new CustomEvent('unreadChatsChanged'));
    }
    
  } catch (error) {
    console.error('Error checking unread:', error);
  } finally {
    window.isCheckingUnread = false;
  }
};

// Check for unread messages across all chats
export const checkUnreadMessages = async () => {
  // 🛑 Don't check if ANY chat is currently open
  if (window.currentOpenChatId) {
    return; // Silent guard, no logging
  }
  
  try {
    const supabaseClient = window.supabase || supabase;
    const currentUserId = window.currentAppUser?.uid || window.currentUser?.uid;
    
    if (!currentUserId) return;
    
    // Get all events where user is participant or organizer
    const { data: events } = await supabaseClient
      .from('events')
      .select('event_id')
      .or(`creator_id.eq.${currentUserId},participants.cs.{${currentUserId}}`)
      .eq('archived', false);
    
    
    if (!events || events.length === 0) {
      window.hasUnreadChats = false;
      return;
    }
    
    // Check each event for unread messages
    for (const event of events) {
      const { data: unreadMessages } = await supabaseClient
        .from('messages')
        .select('id, read_by, user_id')
        .eq('event_id', event.event_id)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (unreadMessages && unreadMessages.length > 0) {
        const msg = unreadMessages[0];
        const readBy = msg.read_by || [];
        
        // If message is from someone else and not read by current user
        if (msg.user_id !== currentUserId && !readBy.includes(currentUserId)) {
          window.hasUnreadChats = true;
          
          // Force re-render of header
          const event = new CustomEvent('unreadChatsChanged');
          window.dispatchEvent(event);
          return;
        }
      }
    }
    
    window.hasUnreadChats = false;
  } catch (error) {
    console.error('Error checking unread messages:', error);
  }
};

export const openChatFromList = async (eventId, eventTitle) => {
  // Close chats list panel
  window.closeChatsListPanel();
  
  // Wait for animation
  await new Promise(resolve => setTimeout(resolve, 350));
  
  // SET FLAGS - we opened from chats list, NOT from event details
  window.openedFromChatsList = true;
  window.openedEventDetailsFromChat = false;
  
  // Use helper to open and clear indicator
  await window.openChatAndClearIndicator(eventId, eventTitle);
};

window.loadChatsListDebounced = loadChatsListDebounced;
window.openChatsListPanel = openChatsListPanel;
window.closeChatsListPanel = closeChatsListPanel;
window.loadChatsList = loadChatsList;
window.checkUnreadMessagesQuick = checkUnreadMessagesQuick;
// Check unread on initial load
window.checkUnreadMessages = checkUnreadMessages;
checkUnreadMessages(); // auto-run on import
window.openChatFromList = openChatFromList;